import { a } from "../../../shared/globalState/a";
import { Block } from "../../../models/shapes/Block";
import { Line } from "../../../models/shapes/Line";
import { ElbowBlock } from "../../../models/resistances/ElbowBlock";
import { Tee } from "../../../models/resistances/Tee";
import { Shape } from "../../../shared/types";
import { Branch } from "../../../models/nets/Branch";
import { ObjectTypes } from "../../../shared/enums";

/**
 * Filter block elements from mixed element array
 */
export function filterBlockElements(elementsToPerformCalculations: (Line | Block | ElbowBlock | Tee)[]): Block[] {
  return elementsToPerformCalculations.filter((el): el is Block => el instanceof Block && !(el instanceof ElbowBlock) && !(el instanceof Tee));
}

/**
 * Get flow rate from input field or preNetInfo
 */
export function getFlowRate(flowRateInput: number | null | undefined): number {
  if (flowRateInput !== undefined && flowRateInput !== null && !isNaN(flowRateInput)) {
    return flowRateInput;
  }

  // Try to get from input field first
  const flowRateInputElement = document.getElementById("flow-rate") as HTMLInputElement;
  if (flowRateInputElement && flowRateInputElement.value) {
    const value = flowRateInputElement.valueAsNumber;
    if (!isNaN(value)) {
      return value;
    }
  }

  // Fallback to a.preNetInfo.flowRateCmh
  if (a.preNetInfo && a.preNetInfo.flowRateCmh !== undefined && a.preNetInfo.flowRateCmh !== null) {
    return a.preNetInfo.flowRateCmh;
  }

  // Final fallback to 0
  console.warn(`** flowRate is undefined - using default value 0`);
  return 0;
}

/**
 * Perform network calculations for selected elements
 */
export function performNetworkCalculations(
  netElements: (Line | Block | ElbowBlock | Tee)[],
  flowRate: number | null | undefined
): void {
  if (!netElements || !netElements.length) {
    return;
    }
    
    

  const calculatedFlowRate = getFlowRate(flowRate);

  // Find a Block element and get its graph safely
  const blockElement = netElements.find((ne) => ne instanceof Block) as Block | undefined;
  if (!blockElement) {
    console.error(`** No Block element found in 'performNetworkCalculations' method - return`);
    return;
  }

  const connectedSegments = blockElement.getConnectedSegments();
  const firstSegment = connectedSegments?.find((seg) => seg !== undefined && seg !== null);
  if (!firstSegment) {
    console.error(`** No connected segments found in 'performNetworkCalculations' method - return`);
    return;
  }

  const graph = firstSegment.getGraph();
  if (!graph) {
    console.error(`** No graph found in 'performNetworkCalculations' method - return`);
    return;
  }

  const elementsToPerformCalculations = graph.getAllElements();

  // Step 0: Clear all tee flow direction types for all branches before recalculating
  // This ensures old values don't persist when network structure changes
  // Clear the entire flowDirectionType map for all tees to remove any stale branch IDs
  if (elementsToPerformCalculations) {
    const allTees = elementsToPerformCalculations.filter((el) => el instanceof Tee) as Tee[];
    allTees.forEach((tee) => {
      if (tee.flowDirectionType) {
        // Clear all flow direction types (removes entries for old branches too)
        tee.flowDirectionType.clear();
      }
    });
  }

  // Assigning flow rate to terminals - preserve individual flow rates, only set default for terminals without one
  const blockElements = filterBlockElements(elementsToPerformCalculations);
  const terminalElements = blockElements.filter((el: Block) => el.checkIsTerminal());
  terminalElements.forEach((terminal) => {
    // Only assign flow rate if terminal doesn't already have one set
    if (!terminal.net.flowRateCmh || terminal.net.flowRateCmh === 0) {
      terminal.net.flowRateCmh = calculatedFlowRate;
    }
  });

  // Calculation and assignment flow in branches
  // get branches for terminalElements (filter out null values)
  const branches = terminalElements.map((terminal) => Branch.getBranchByElement(terminal)).filter((b): b is Branch => b !== null);

  // First, calculate endFlowRate for all branches
  branches.forEach((b) => {
    b.calculateEndFlowRate(calculatedFlowRate);
  });

  Branch.assignCumulativeFlowRates();

  // Now we need to update tee sizes, assign tee inlet flow rates, and calculate tee dp
  // Process all branches (not just terminal branches) since tees can be shared
  if (a.branches && a.branches.length > 0) {
    a.branches.forEach((branch: Branch) => {
      // Step 0: Assign tee flow direction types first ("on pass" vs "on turn" for each branch)
      // This must be done before assigning flows, as it determines how tees behave in each branch
        branch.assignTeeFlowDirectionType();
        
        // Print branch's tees flow direction types
        const branchTees = branch.elements.filter((el) => el instanceof Tee) as Tee[];
        if (branchTees.length > 0) {
          console.log(`[Tee Flow Directions] Branch ${branch.id} - Tees flow direction types:`);
          branchTees.forEach((tee) => {
            const flowType = tee.flowDirectionType?.get(branch.id);
            const exhaustOrSupply = tee.exhaustOrSupply === ObjectTypes.exhaust ? "exhaust" : tee.exhaustOrSupply === ObjectTypes.supply ? "supply" : "UNKNOWN";
            console.log(`  - Tee ${tee.id}: flowType=${flowType || 'UNKNOWN'}, exhaustOrSupply=${exhaustOrSupply}`);
          });
        }

      // Step 0.5: Assign exhaust/supply type to tees
      branch.assignTeeTypeExhaustOrSupply();

      // Step 1: Update tee sizes from connected segments
      branch.assignTeeSize();

      // Step 2: Assign tee inlet flow rates (collector and turn flows)
      branch.assignTeeFlowsOnCrucialPoints();

      // Step 3: Calculate tee dp using assigned flows, sizes, and velocities
      branch.calculateTeeDp();

      branch.calculateDp();
    });
  }
}

/**
 * Назначает параметры сети (расход и размеры) выбранным элементам
 *
 * В разветвлённой сети:
 * - Расход назначается только терминалам (Block с checkIsTerminal())
 * - Расход для остальных элементов рассчитывается автоматически через Branch.assignCumulativeFlowRates()
 * - Размеры назначаются всем выбранным элементам (Line и Block)
 *
 * @param selectedNetElements - Массив выбранных элементов сети
 * @param flowRate - Расход воздуха (м³/ч)
 * @param isRect - Флаг прямоугольного сечения
 * @param isRound - Флаг круглого сечения
 * @param width - Ширина для прямоугольного сечения (мм)
 * @param height - Высота для прямоугольного сечения (мм)
 * @param diameter - Диаметр для круглого сечения (мм)
 */
export function assignNetworkParameters(
  selectedNetElements: Shape[],
  flowRate: number,
  isRect: boolean,
  isRound: boolean,
  width: number,
  height: number,
  diameter: number
): void {
  const elements = selectedNetElements.filter((s: Shape) => s instanceof Line || s instanceof Block);

  elements.forEach((ne: Line | Block) => {
    if (ne.net) {
      // В разветвлённой сети расход назначается только терминалам
      // Для остальных элементов расход рассчитывается автоматически на основе расходов терминалов
      if (ne instanceof Block) {
        const block = ne as Block;
        // Назначаем расход только терминальным блокам
        if (block.checkIsTerminal && block.checkIsTerminal()) {
          block.net.flowRateCmh = flowRate;
        }
      }

      // Назначаем размеры всем элементам (Line и Block)
      if (isRect) {
        ne.net.widthMil = width;
        ne.net.heightMil = height;
      } else if (isRound) {
        ne.net.diameterMil = diameter;
      }
    }
  });
}

